import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { Reorder, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import ManageShortcuts from "./ManageShortcuts";
import { NavIcon, SHORTCUT_LABELS } from "./navIcons";
import { NavGroup, NavPage } from "./navModel";
import { isPlainClick } from "./navPrimitives";
import { NAV, resetRow } from "./navTheme";
import { setShortcutOrder, unpinShortcut } from "./shortcutStore";

interface QuickShortcutsProps {
  shortcuts: NavPage[];
  /** Every page the user may pin — drives the "+" picker. */
  groups: NavGroup[];
  /** Touch layout: taller tiles, bigger icons, momentum scrolling. */
  touch?: boolean;
  onNavigate?: () => void;
}

/**
 * Pinned pages as one scrolling row of tiles. Long-press (touch) or right-click
 * enters edit mode: remove badges appear and tiles become drag-to-reorder.
 */
const QuickShortcuts: React.FC<QuickShortcutsProps> = ({
  shortcuts,
  groups,
  touch = false,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const longPressRef = useRef<number | null>(null);

  const pinnedIds = shortcuts.map((page) => page.id);

  // Nothing left to edit once the row is empty.
  useEffect(() => {
    if (shortcuts.length === 0) setEditing(false);
  }, [shortcuts.length]);

  const cancelLongPress = useCallback(() => {
    if (longPressRef.current !== null) {
      window.clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  }, []);

  useEffect(() => cancelLongPress, [cancelLongPress]);

  const handleReorder = useCallback((next: NavPage[]) => {
    setShortcutOrder(next.map((page) => page.id));
  }, []);

  const tileWidth = touch ? 72 : 60;
  const tileHeight = touch ? 64 : 56;

  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    flex: `0 0 ${tileWidth}px`,
    scrollSnapAlign: "start",
  };

  const renderTile = (page: NavPage) => {
    const isActive = pathname === page.link;
    return (
      <>
        <Box
          component={motion.a}
          href={page.link}
          // Links are natively draggable; that would hijack framer's pointer drag.
          draggable={false}
          whileTap={editing ? undefined : { scale: 0.94 }}
          animate={editing ? { rotate: [0, -1.1, 0, 1.1, 0] } : { rotate: 0 }}
          transition={
            editing
              ? { duration: 0.32, repeat: Infinity, ease: "linear" }
              : NAV.spring
          }
          onClick={(event: React.MouseEvent) => {
            if (!isPlainClick(event)) return;
            event.preventDefault();
            if (editing) return;
            navigate(page.link);
            onNavigate?.();
          }}
          onPointerDown={(event: React.PointerEvent) => {
            // Touch gets long-press; mouse gets hover + right-click.
            if (editing || event.pointerType === "mouse") return;
            cancelLongPress();
            longPressRef.current = window.setTimeout(
              () => setEditing(true),
              550,
            );
          }}
          onPointerUp={cancelLongPress}
          onPointerLeave={cancelLongPress}
          onPointerCancel={cancelLongPress}
          onContextMenu={(event: React.MouseEvent) => {
            event.preventDefault();
            setEditing(true);
          }}
          aria-current={isActive ? "page" : undefined}
          title={editing ? `Drag to reorder ${page.label}` : page.label}
          sx={{
            ...resetRow,
            width: "100%",
            height: tileHeight,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            borderRadius: "14px",
            cursor: editing ? "grab" : "pointer",
            "&:active": { cursor: editing ? "grabbing" : "pointer" },
            color: isActive ? NAV.accent : NAV.textIdle,
            backgroundColor: isActive ? NAV.accentSoft : NAV.hover,
            border: `1px solid ${isActive ? NAV.accentBorder : "transparent"}`,
            boxShadow: isActive ? NAV.accentGlow : "none",
            transition: `background-color 180ms ${NAV.ease}, color 180ms ${NAV.ease}, border-color 180ms ${NAV.ease}`,
            "@media (hover: hover)": {
              "&:hover": {
                backgroundColor: isActive ? NAV.accentSoft : NAV.pressed,
                color: isActive ? NAV.accent : NAV.textActive,
              },
            },
          }}
        >
          <NavIcon
            id={page.id}
            groupId={page.groupId}
            size={touch ? 24 : 21}
            color="inherit"
          />
          <Typography
            sx={{
              fontSize: touch ? "0.6875rem" : "0.625rem",
              fontWeight: isActive ? 600 : 500,
              lineHeight: 1,
              letterSpacing: "0.01em",
              color: "inherit",
              maxWidth: "100%",
              px: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {SHORTCUT_LABELS[page.id] ?? page.label}
          </Typography>
        </Box>

        <Box
          component="button"
          type="button"
          className="shortcut-remove"
          data-editing={editing ? "true" : "false"}
          aria-label={`Remove ${page.label} shortcut`}
          title={`Remove ${page.label}`}
          onClick={(event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            unpinShortcut(page.id);
          }}
          onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
          sx={{
            ...resetRow,
            position: "absolute",
            top: -5,
            right: -2,
            width: 18,
            height: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            color: NAV.textActive,
            backgroundColor: NAV.surfaceRaised,
            border: `1px solid ${NAV.borderStrong}`,
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.45)",
            opacity: 0,
            pointerEvents: "none",
            transition: `opacity 140ms ${NAV.ease}, background-color 140ms ${NAV.ease}`,
            "&:hover": { backgroundColor: "#3a2626", color: "#ff9d9d" },
          }}
        >
          <CloseRounded sx={{ fontSize: 12 }} />
        </Box>
      </>
    );
  };

  return (
    <Box
      ref={rowRef}
      sx={{
        px: touch ? 1.5 : 1.75,
        pb: 1.5,
        // Scoped here: the tiles carry plain styles, not `sx`.
        "@media (hover: hover)": {
          "& .shortcut-tile:hover .shortcut-remove": {
            opacity: 1,
            pointerEvents: "auto",
          },
        },
        "& .shortcut-remove[data-editing='true']": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
    >
      {/* Scroller wraps the group, not is it — framer walks up for a
          scrollable ancestor to auto-scroll during drag. */}
      <Box
        sx={{
          overflowX: "auto",
          overscrollBehaviorX: "contain",
          WebkitOverflowScrolling: "touch",
          scrollSnapType: touch && !editing ? "x proximity" : "none",
          // Room for the remove badge, which sits proud of the tile.
          pt: 0.875,
          pb: 0.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Reorder.Group
          as="div"
          axis="x"
          values={shortcuts}
          onReorder={handleReorder}
          style={{
            display: "flex",
            gap: 6,
            width: "max-content",
            minWidth: "100%",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
        >
          {shortcuts.map((page) =>
            editing ? (
              <Reorder.Item
                key={page.id}
                as="div"
                value={page}
                className="shortcut-tile"
                whileDrag={{ scale: 1.08 }}
                style={wrapperStyle}
              >
                {renderTile(page)}
              </Reorder.Item>
            ) : (
              <div key={page.id} className="shortcut-tile" style={wrapperStyle}>
                {renderTile(page)}
              </div>
            ),
          )}

          <Box
            component="button"
            type="button"
            aria-label="Add a shortcut"
            title="Add a shortcut"
            onClick={() => {
              setEditing(false);
              setPickerOpen(true);
            }}
            sx={{
              ...resetRow,
              flex: `0 0 ${shortcuts.length === 0 ? tileWidth + 40 : tileWidth}px`,
              height: tileHeight,
              scrollSnapAlign: "start",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              borderRadius: "14px",
              color: NAV.textMuted,
              border: `1px dashed ${NAV.borderStrong}`,
              transition: `color 180ms ${NAV.ease}, border-color 180ms ${NAV.ease}, background-color 180ms ${NAV.ease}`,
              "@media (hover: hover)": {
                "&:hover": {
                  color: NAV.accent,
                  borderColor: NAV.accentBorder,
                  backgroundColor: NAV.accentSofter,
                },
              },
            }}
          >
            <AddRounded sx={{ fontSize: touch ? 24 : 21 }} />
            <Typography
              sx={{
                fontSize: touch ? "0.6875rem" : "0.625rem",
                fontWeight: 500,
                lineHeight: 1,
                color: "inherit",
              }}
            >
              {shortcuts.length === 0 ? "Add shortcuts" : "Add"}
            </Typography>
          </Box>
        </Reorder.Group>
      </Box>

      {editing && (
        <Box
          component="button"
          type="button"
          onClick={() => setEditing(false)}
          sx={{
            ...resetRow,
            display: "block",
            width: "100%",
            mt: 0.25,
            fontSize: "0.6875rem",
            fontWeight: 600,
            textAlign: "center",
            color: NAV.accent,
          }}
        >
          Drag to reorder · Done
        </Box>
      )}

      <ManageShortcuts
        open={pickerOpen}
        // Anchored to the row: the "+" tile shifts as shortcuts are added.
        anchorEl={rowRef.current}
        groups={groups}
        pinnedIds={pinnedIds}
        onClose={() => setPickerOpen(false)}
      />
    </Box>
  );
};

export default QuickShortcuts;
